import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Home,
  Stethoscope,
  Mail,
  Check,
  ArrowRight,
  Lock,
  User,
  ArrowLeft,
  ChevronRight,
  ShieldCheck,
  Phone,
  Building2,
  MessageSquare,
} from "lucide-react";
import { waitlist as waitlistApi } from "@/lib/api";
import PasswordInput from "@/components/PasswordInput";
import BrandedLoader from "@/components/BrandedLoader";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import PasswordRequirements, { isPasswordValid } from "@/components/PasswordRequirements";
import TermsDialog from "@/components/TermsDialog";
import SEO from "@/components/SEO";
import useAppReady from "@/lib/useAppReady";
import { getRandomQuote, parseHeadline } from "@/lib/quotes";

const LOGO     = "/assets/images/kiddo-logo.png";
const WORDMARK = "/assets/images/kiddo-wordmark.png";

/* ── Shared mobile style constants ─────────────────────────── */
const INPUT_STYLE = {
  borderRadius: "9999px",
  paddingTop: "0.85rem",
  paddingBottom: "0.85rem",
  paddingLeft: "3rem",
  paddingRight: "1rem",
};

/**
 * MobileHero — quote-hero top + white bottom-sheet card.
 * Used by all three signup steps on mobile.
 */
function MobileHero({ children, topExtra = null, revealed = true }) {
  const quote = useMemo(() => getRandomQuote(), []);

  return (
    <div
      className={`min-h-[100dvh] flex flex-col transition-opacity duration-300 bg-[#EBF5F2] dark:bg-[#091714] ${revealed ? "opacity-100" : "opacity-0"}`}
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      data-testid="mobile-auth-layout"
      data-kp-auth-page="true"
    >
      {topExtra && <div className="px-5 pt-5">{topExtra}</div>}

      {/* Quote section */}
      <div className="flex-shrink-0 px-7 pt-10 pb-8 flex flex-col items-center text-center">
        <span
          className="block leading-none mb-3 select-none text-[#2A7B6F] dark:text-teal-400"
          style={{ fontSize: "3.4rem", fontFamily: 'Georgia, "Times New Roman", serif', lineHeight: 0.85 }}
        >❝</span>

        <h1 className="text-[1.55rem] font-black leading-snug tracking-tight text-[#1A2E2A] dark:text-[#DFF2EE]">
          {quote.headline.split("\n").map((line, li, arr) => (
            <span key={li}>
              {parseHeadline(line).map((part, pi) =>
                part.teal
                  ? <span key={pi} className="text-[#2A7B6F] dark:text-teal-400">{part.text}</span>
                  : part.text
              )}
              {li < arr.length - 1 && <br />}
            </span>
          ))}
        </h1>

        <div className="my-4 rounded-full bg-[#2A7B6F] dark:bg-teal-400" style={{ width: 38, height: 2.5 }} />

        {(() => {
          const parts = quote.tagline.split("\n");
          return parts.length >= 2 ? (
            <>
              <p className="text-sm leading-relaxed text-[#4A5568] dark:text-gray-400">{parts[0]}</p>
              <p className="text-sm font-medium text-[#2A7B6F] dark:text-teal-400">{parts[1]}</p>
            </>
          ) : (
            <p className="text-sm font-medium text-[#2A7B6F] dark:text-teal-400">{parts[0]}</p>
          );
        })()}
      </div>

      {/* White bottom-sheet */}
      <div
        className="bg-white dark:bg-kp-surface flex-1 overflow-y-auto"
        style={{ borderRadius: "2rem 2rem 0 0", boxShadow: "0 -4px 32px rgba(0,0,0,0.08)" }}
      >
        <div className="flex justify-center pt-3 pb-1">
          <div className="rounded-full bg-gray-300 dark:bg-gray-600" style={{ width: 36, height: 4 }} />
        </div>
        <div className="px-6 pt-4 pb-10">{children}</div>
      </div>
    </div>
  );
}

/** Shared privacy badge used in multiple steps */
function PrivacyBadge({ icon = "lock", title = "We respect your privacy", desc = "Your child's data is encrypted and protected." }) {
  return (
    <div className="mt-6 flex items-center gap-4 p-4 rounded-2xl bg-[#EBF5F2] dark:bg-teal-950/40">
      <div className="flex items-center justify-center shrink-0 rounded-full bg-teal-600/[.12] dark:bg-teal-400/20" style={{ width: 44, height: 44 }}>
        {icon === "shield"
          ? <ShieldCheck className="text-[#2A7B6F] dark:text-teal-400" style={{ width: 22, height: 22 }} />
          : <Lock className="text-[#2A7B6F] dark:text-teal-400" style={{ width: 22, height: 22 }} />}
      </div>
      <div>
        <p className="text-sm font-bold text-[#2A7B6F] dark:text-teal-400">{title}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mt-0.5">{desc}</p>
      </div>
    </div>
  );
}

/**
 * DesktopLayout — 50/50 mint split matching the login desktop redesign.
 * Left: rotating quote. Right: white card with children.
 */
function DesktopLayout({ children, topExtra = null }) {
  const quote = useMemo(() => getRandomQuote(), []);
  return (
    <div className="hidden md:flex min-h-screen bg-[#EBF5F2] dark:bg-[#091714]" data-kp-auth-page="true">
      {/* Left half: quote */}
      <div className="w-1/2 flex flex-col px-16 xl:px-24 py-12">
        <Link to="/" className="inline-flex items-center gap-2 shrink-0">
          <img src={LOGO} alt="Kiddo+" className="w-10 h-10 rounded-[22%]" />
          <img src={WORDMARK} alt="Kiddo+" className="h-8 w-auto dark:brightness-0 dark:invert" />
        </Link>

        {topExtra && <div className="mt-8">{topExtra}</div>}

        <div className="flex-1 flex flex-col justify-center">
          <span
            className="block leading-none mb-5 select-none text-[#2A7B6F] dark:text-teal-400"
            style={{ fontSize: "5rem", fontFamily: 'Georgia, "Times New Roman", serif', lineHeight: 0.8 }}
          >❝</span>
          <h1
            className="font-black leading-tight tracking-tight mb-5 text-[#1A2E2A] dark:text-[#DFF2EE]"
            style={{ fontSize: "clamp(2rem, 3.2vw, 3rem)" }}
          >
            {quote.headline.split("\n").map((line, li, arr) => (
              <span key={li}>
                {parseHeadline(line).map((part, pi) =>
                  part.teal ? <span key={pi} className="text-[#2A7B6F] dark:text-teal-400">{part.text}</span> : part.text
                )}
                {li < arr.length - 1 && <br />}
              </span>
            ))}
          </h1>
          <div className="rounded-full mb-5 bg-[#2A7B6F] dark:bg-teal-400" style={{ width: 52, height: 3 }} />
          {(() => {
            const parts = quote.tagline.split("\n");
            return parts.length >= 2
              ? <p className="text-lg font-medium leading-relaxed text-[#2A7B6F] dark:text-teal-400">{parts[0]} {parts[1]}</p>
              : <p className="text-lg font-medium text-[#2A7B6F] dark:text-teal-400">{parts[0]}</p>;
          })()}
        </div>
      </div>

      {/* Right half: floating card */}
      <div className="w-1/2 flex items-center justify-center px-8 xl:px-10 py-12">
        <div
          className="w-full rounded-3xl bg-white dark:bg-kp-surface p-10 xl:p-12 auth-desktop-card"
          style={{ maxWidth: 680 }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export default function Signup() {
  useAppReady();

  const { signup, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next");

  useEffect(() => {
    if (user) {
      if (nextPath && nextPath.startsWith("/")) navigate(nextPath, { replace: true });
      else navigate(user.role === "admin" ? "/admin" : "/activities", { replace: true });
    }
  }, [user, navigate, nextPath]);

  const [step, setStep] = useState(1);
  const [data, setData] = useState({ full_name: "", email: "", password: "" });
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pwdFocused, setPwdFocused] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const pwdValid = isPasswordValid(data.password);

  const submit = async () => {
    if (!agree) { toast.error("Please agree to the Terms & Conditions to continue."); return; }
    if (!pwdValid) { toast.error("Please choose a password that meets all the requirements."); return; }
    setLoading(true);
    try {
      await signup({ ...data, role: "parent" });
      toast.success("Account created! Let's set up your child's profile.");
      if (nextPath && nextPath.startsWith("/")) navigate(nextPath);
      else navigate("/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  /* ── STEP 1 — Role selection ──────────────────────────────── */
  if (step === 1) {
    return (
      <>
        <SEO
          title="Sign Up Free — Start Your Child's Free Plan in 5 Minutes"
          description="Create your free Kiddo+ account. Get a developmental assessment and personalised activities."
          path="/signup"
          keywords="sign up kiddo plus, create child development account, free assessment signup"
        />

        {/* MOBILE — quote-hero + white card with role picker */}
        <div className="md:hidden">
          <MobileHero>
            {/* Role cards */}
            <div className="flex flex-col gap-4" data-testid="role-select">

              {/* Parent card */}
              <button
                onClick={() => setStep(2)}
                data-testid="role-parent"
                className="w-full text-left rounded-2xl py-5 px-4 flex items-center gap-4 active:opacity-80 transition-opacity bg-[#EBF5F2] dark:bg-teal-950/40 border border-[#C2E0DA] dark:border-teal-700/40"
              >
                <div className="flex items-center justify-center shrink-0 rounded-full bg-teal-600/15 dark:bg-teal-400/20" style={{ width: 52, height: 52 }}>
                  <Home className="text-[#2A7B6F] dark:text-teal-400" style={{ width: 24, height: 24 }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[9px] font-extrabold uppercase tracking-[0.14em] mb-1 text-[#2A7B6F] dark:text-teal-400">
                    Most parents start here
                  </p>
                  <p className="text-base font-black leading-tight mb-1 text-[#1A2E2A] dark:text-[#DFF2EE]">
                    Parent or Caregiver
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-snug">
                    Free to start, personalised for your child.
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
              </button>

              {/* Therapist card */}
              <button
                onClick={() => setStep(3)}
                data-testid="role-therapist"
                className="w-full text-left rounded-2xl py-5 px-4 flex items-center gap-4 active:opacity-80 transition-opacity bg-[#FFF5EE] dark:bg-orange-950/30 border border-[#FDDBC6] dark:border-orange-700/40"
              >
                <div className="flex items-center justify-center shrink-0 rounded-full bg-orange-500/[.12] dark:bg-orange-400/15" style={{ width: 52, height: 52 }}>
                  <Stethoscope className="text-[#EA7843] dark:text-orange-400" style={{ width: 24, height: 24 }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[9px] font-extrabold uppercase tracking-[0.14em] mb-1 text-[#EA7843] dark:text-orange-400">
                    Invite-only
                  </p>
                  <p className="text-base font-black leading-tight mb-1 text-[#1A2E2A] dark:text-[#DFF2EE]">
                    Therapist or Educator
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-snug">
                    Join the waitlist — we&apos;ll reach out when slots open.
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
              </button>
            </div>

            {/* Sign in */}
            <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
              Already with us?{" "}
              <Link to="/login" className="font-bold text-[#2A7B6F] dark:text-teal-400" data-testid="login-link">Sign in</Link>
            </p>

            <PrivacyBadge icon="shield" title="Your data is safe with us" desc="We never share your information with third parties." />
          </MobileHero>
        </div>

        {/* DESKTOP — 50/50 mint split */}
        <DesktopLayout>
          <h2 className="text-2xl font-black mb-1 text-[#1A2E2A] dark:text-[#DFF2EE]">Who&apos;s signing up?</h2>
          <p className="text-sm mb-6 text-gray-500 dark:text-gray-400">Pick the path that fits your journey.</p>

          <div className="flex flex-col gap-4" data-testid="role-select">
            {/* Parent card */}
            <button
              onClick={() => setStep(2)}
              data-testid="role-parent"
              className="w-full text-left rounded-2xl py-5 px-5 flex items-center gap-4 transition-all hover:scale-[1.01] active:scale-[0.99] bg-[#EBF5F2] dark:bg-teal-950/40 border border-[#C2E0DA] dark:border-teal-700/40"
            >
              <div className="flex items-center justify-center shrink-0 rounded-full bg-teal-600/15 dark:bg-teal-400/20" style={{ width: 52, height: 52 }}>
                <Home className="text-[#2A7B6F] dark:text-teal-400" style={{ width: 24, height: 24 }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[9px] font-extrabold uppercase tracking-[0.14em] mb-1 text-[#2A7B6F] dark:text-teal-400">Most parents start here</p>
                <p className="text-base font-black leading-tight mb-1 text-[#1A2E2A] dark:text-[#DFF2EE]">Parent or Caregiver</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Free to start, personalised for your child.</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
            </button>

            {/* Therapist card */}
            <button
              onClick={() => setStep(3)}
              data-testid="role-therapist"
              className="w-full text-left rounded-2xl py-5 px-5 flex items-center gap-4 transition-all hover:scale-[1.01] active:scale-[0.99] bg-[#FFF5EE] dark:bg-orange-950/30 border border-[#FDDBC6] dark:border-orange-700/40"
            >
              <div className="flex items-center justify-center shrink-0 rounded-full bg-orange-500/[.12] dark:bg-orange-400/15" style={{ width: 52, height: 52 }}>
                <Stethoscope className="text-[#EA7843] dark:text-orange-400" style={{ width: 24, height: 24 }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[9px] font-extrabold uppercase tracking-[0.14em] mb-1 text-[#EA7843] dark:text-orange-400">Invite-only</p>
                <p className="text-base font-black leading-tight mb-1 text-[#1A2E2A] dark:text-[#DFF2EE]">Therapist or Educator</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Join the waitlist — we&apos;ll reach out when slots open.</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
            </button>
          </div>

          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
            Already with us?{" "}
            <Link to="/login" className="font-bold text-[#2A7B6F] dark:text-teal-400" data-testid="login-link-desktop">Sign in</Link>
          </p>

          <div className="mt-5 flex items-center gap-3 p-3.5 rounded-2xl bg-[#EBF5F2] dark:bg-teal-950/40">
            <div className="flex items-center justify-center shrink-0 rounded-full bg-teal-600/[.12] dark:bg-teal-400/20" style={{ width: 38, height: 38 }}>
              <ShieldCheck className="text-[#2A7B6F] dark:text-teal-400" style={{ width: 19, height: 19 }} />
            </div>
            <div>
              <p className="text-xs font-bold text-[#2A7B6F] dark:text-teal-400">Your data is safe with us</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">We never share your information with third parties.</p>
            </div>
          </div>
        </DesktopLayout>
      </>
    );
  }

  /* ── STEP 2 — Parent signup form ──────────────────────────── */
  if (step === 2) {
    return (
      <>
        <BrandedLoader visible={loading} title="Creating your account…" subtitle="Setting up your Kiddo+ space." />

        {/* MOBILE — quote-hero + white card */}
        <div className="md:hidden">
          <MobileHero
            topExtra={
              <button
                type="button"
                onClick={() => setStep(1)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2A7B6F] dark:text-teal-400"
                data-testid="change-role-link"
              >
                <ArrowLeft className="w-4 h-4" /> Change role
              </button>
            }
          >
            {/* Google */}
            <GoogleSignInButton
              label="Sign up with Google"
              className="!rounded-full !py-[1.7vh] mb-5 !bg-[#ECEDF8] dark:!bg-[#1A2535]"
              style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.09), 0 1px 2px rgba(0,0,0,0.06)" }}
            />

            {/* "or" divider */}
            <div className="flex items-center gap-3 mb-5">
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
              <span className="text-sm text-gray-400 dark:text-gray-500">or</span>
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
            </div>

            {/* Form — no labels, just icon inputs (matches reference) */}
            <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="flex flex-col gap-4">

              {/* Full name */}
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" style={{ width: 18, height: 18 }} />
                <Input
                  id="m-name"
                  type="text"
                  placeholder="Enter your full name"
                  value={data.full_name}
                  onChange={(e) => setData({ ...data, full_name: e.target.value })}
                  required
                  className="h-auto text-sm placeholder:text-gray-400 focus-visible:ring-offset-0"
                  style={INPUT_STYLE}
                  data-testid="signup-name-input"
                />
              </div>

              {/* Email */}
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" style={{ width: 18, height: 18 }} />
                <Input
                  id="m-sig-email"
                  type="email"
                  placeholder="Enter your email"
                  value={data.email}
                  onChange={(e) => setData({ ...data, email: e.target.value })}
                  required
                  className="h-auto text-sm placeholder:text-gray-400 focus-visible:ring-offset-0"
                  style={INPUT_STYLE}
                  data-testid="signup-email-input"
                />
              </div>

              {/* Password */}
              <div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none z-10 text-gray-400" style={{ width: 18, height: 18 }} />
                  <PasswordInput
                    id="m-sig-pwd"
                    value={data.password}
                    onChange={(e) => setData({ ...data, password: e.target.value })}
                    onFocus={() => setPwdFocused(true)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    placeholder="Create a strong password"
                    className="h-auto text-sm placeholder:text-gray-400 focus-visible:ring-offset-0"
                    style={{ ...INPUT_STYLE, paddingRight: undefined }}
                    data-testid="signup-password-input"
                  />
                </div>
                {(pwdFocused || data.password.length > 0) && (
                  <div className="mt-2">
                    <PasswordRequirements value={data.password} variant="tooltip" />
                  </div>
                )}
              </div>

              {/* Terms */}
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  className="w-5 h-5 rounded-md cursor-pointer shrink-0"
                  style={{ accentColor: "#2A7B6F" }}
                  data-testid="signup-terms-checkbox"
                />
                <span className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                  I agree with{" "}
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); setTermsOpen(true); }}
                    className="font-semibold text-[#2A7B6F] dark:text-teal-400"
                    data-testid="signup-terms-link"
                  >
                    Terms &amp; Conditions
                  </button>{" "}
                  and privacy practices.
                </span>
              </label>

              {/* Sign up button */}
              <Button
                type="submit"
                disabled={loading || !agree || !pwdValid}
                className="relative h-auto w-full text-white rounded-full font-bold text-base transition-colors disabled:opacity-50"
                style={{ background: "#2A7B6F", paddingTop: "0.9rem", paddingBottom: "0.9rem" }}
                data-testid="signup-submit-btn"
              >
                {loading ? "Creating…" : "Sign up"}
                {!loading && <ArrowRight className="absolute right-5 top-1/2 -translate-y-1/2 w-5 h-5" />}
              </Button>
            </form>

            {/* Log in link */}
            <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-5">
              Already have an account?{" "}
              <Link to="/login" className="font-bold text-[#2A7B6F] dark:text-teal-400">Log in</Link>
            </p>

            <PrivacyBadge />
          </MobileHero>
        </div>

        {/* DESKTOP — 50/50 mint split */}
        <DesktopLayout>
          <button
            type="button"
            onClick={() => setStep(1)}
            className="inline-flex items-center gap-1.5 text-sm font-medium mb-5 text-[#2A7B6F] dark:text-teal-400"
            data-testid="change-role-link-desktop"
          >
            <ArrowLeft className="w-4 h-4" /> Change role
          </button>

          <h2 className="text-2xl font-black mb-1 text-[#1A2E2A] dark:text-[#DFF2EE]">Create your account</h2>
          <p className="text-sm mb-6 text-gray-500 dark:text-gray-400">Free to start. No credit card needed.</p>

          {/* Google */}
          <GoogleSignInButton
            label="Sign up with Google"
            className="!rounded-full !py-[0.9rem] mb-5 !bg-[#ECEDF8] dark:!bg-[#1A2535]"
            style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.09), 0 1px 2px rgba(0,0,0,0.06)" }}
          />

          {/* "or" divider */}
          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
            <span className="text-sm text-gray-400 dark:text-gray-500">or</span>
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
          </div>

          <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="flex flex-col gap-4">
            {/* Full name */}
            <div>
              <Label htmlFor="d-name" className="block text-sm font-bold mb-2 text-[#1A2E2A] dark:text-[#DFF2EE]">Full Name</Label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" style={{ width: 18, height: 18 }} />
                <Input id="d-name" type="text" placeholder="Enter your full name" value={data.full_name} onChange={(e) => setData({ ...data, full_name: e.target.value })} required
                  className="h-auto text-sm placeholder:text-gray-400 focus-visible:ring-offset-0"
                  style={{ borderRadius: "9999px", paddingTop: "0.85rem", paddingBottom: "0.85rem", paddingLeft: "3rem", paddingRight: "1rem" }}
                  data-testid="signup-name-input-desktop" />
              </div>
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="d-sig-email" className="block text-sm font-bold mb-2 text-[#1A2E2A] dark:text-[#DFF2EE]">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" style={{ width: 18, height: 18 }} />
                <Input id="d-sig-email" type="email" placeholder="Enter your email" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} required
                  className="h-auto text-sm placeholder:text-gray-400 focus-visible:ring-offset-0"
                  style={{ borderRadius: "9999px", paddingTop: "0.85rem", paddingBottom: "0.85rem", paddingLeft: "3rem", paddingRight: "1rem" }}
                  data-testid="signup-email-input-desktop" />
              </div>
            </div>

            {/* Password */}
            <div>
              <Label htmlFor="d-sig-pwd" className="block text-sm font-bold mb-2 text-[#1A2E2A] dark:text-[#DFF2EE]">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none z-10 text-gray-400" style={{ width: 18, height: 18 }} />
                <PasswordInput id="d-sig-pwd" value={data.password} onChange={(e) => setData({ ...data, password: e.target.value })} onFocus={() => setPwdFocused(true)} required minLength={8} autoComplete="new-password" placeholder="Create a strong password"
                  className="h-auto text-sm placeholder:text-gray-400 focus-visible:ring-offset-0"
                  style={{ borderRadius: "9999px", paddingTop: "0.85rem", paddingBottom: "0.85rem", paddingLeft: "3rem" }}
                  data-testid="signup-password-input-desktop" />
              </div>
              {(pwdFocused || data.password.length > 0) && (
                <div className="mt-2"><PasswordRequirements value={data.password} variant="tooltip" /></div>
              )}
            </div>

            {/* Terms */}
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="w-5 h-5 rounded-md cursor-pointer shrink-0" style={{ accentColor: "#2A7B6F" }} data-testid="signup-terms-checkbox-desktop" />
              <span className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                I agree with{" "}
                <button type="button" onClick={(e) => { e.preventDefault(); setTermsOpen(true); }} className="font-semibold text-[#2A7B6F] dark:text-teal-400" data-testid="signup-terms-link-desktop">
                  Terms &amp; Conditions
                </button>{" "}
                and privacy practices.
              </span>
            </label>

            {/* Sign up */}
            <Button type="submit" disabled={loading || !agree || !pwdValid}
              className="relative h-auto w-full text-white rounded-full font-bold text-base transition-colors mt-1 disabled:opacity-50"
              style={{ background: "#2A7B6F", paddingTop: "0.9rem", paddingBottom: "0.9rem" }}
              data-testid="signup-submit-btn-desktop">
              {loading ? "Creating…" : "Sign up"}
              {!loading && <ArrowRight className="absolute right-5 top-1/2 -translate-y-1/2 w-5 h-5" />}
            </Button>
          </form>

          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-5">
            Already have an account?{" "}
            <Link to="/login" className="font-bold text-[#2A7B6F] dark:text-teal-400">Log in</Link>
          </p>

          <div className="mt-5 flex items-center gap-3 p-3.5 rounded-2xl bg-[#EBF5F2] dark:bg-teal-950/40">
            <div className="flex items-center justify-center shrink-0 rounded-full bg-teal-600/[.12] dark:bg-teal-400/20" style={{ width: 38, height: 38 }}>
              <ShieldCheck className="text-[#2A7B6F] dark:text-teal-400" style={{ width: 19, height: 19 }} />
            </div>
            <div>
              <p className="text-xs font-bold text-[#2A7B6F] dark:text-teal-400">Your data is safe with us</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">We never share your information.</p>
            </div>
          </div>
        </DesktopLayout>

        <TermsDialog open={termsOpen} onOpenChange={setTermsOpen} />
      </>
    );
  }

  /* ── STEP 3 — Therapist waitlist ──────────────────────────── */
  return (
    <>
      {/* MOBILE — quote-hero + white card with waitlist form */}
      <div className="md:hidden">
        <MobileHero
          topExtra={
            <button
              type="button"
              onClick={() => setStep(1)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2A7B6F] dark:text-teal-400"
              data-testid="change-role-link"
            >
              <ArrowLeft className="w-4 h-4" /> Change role
            </button>
          }
        >
          <TherapistWaitlist onBack={() => setStep(1)} />
        </MobileHero>
      </div>

      {/* DESKTOP — 50/50 mint split */}
      <DesktopLayout>
        <TherapistWaitlist onBack={() => setStep(1)} backButton={
          <button
            type="button"
            onClick={() => setStep(1)}
            className="inline-flex items-center gap-1.5 text-sm font-medium mb-5 text-[#2A7B6F] dark:text-teal-400"
            data-testid="change-role-link-desktop"
          >
            <ArrowLeft className="w-4 h-4" /> Change role
          </button>
        } />
      </DesktopLayout>
    </>
  );
}

function TherapistWaitlist({ onBack, backButton = null }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "", note: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) return;
    const digits = form.phone.replace(/[^0-9]/g, "");
    if (digits.length < 10) {
      toast.error("Please enter a valid mobile number (at least 10 digits).");
      return;
    }
    setLoading(true);
    try {
      const r = await waitlistApi.join({ ...form, type: "therapist" });
      toast.success(r.already ? "You're already on the waitlist." : "You're on the list!");
      setSubmitted(true);
    } catch {
      toast.error("Could not join waitlist. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center" data-testid="waitlist-success">
        <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 bg-[#EBF5F2] dark:bg-teal-950/40">
          <Check className="w-10 h-10 text-[#2A7B6F] dark:text-teal-400" />
        </div>
        <h2 className="text-2xl font-black mb-2 text-[#1A2E2A] dark:text-[#DFF2EE]">You&apos;re on the waitlist</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Thanks! We&apos;ll email <strong className="text-gray-700 dark:text-gray-300">{form.email}</strong> when therapist invites open.
        </p>
        <div className="mt-6 flex flex-col gap-3">
          <button type="button" onClick={onBack} className="inline-flex items-center justify-center gap-1.5 text-sm font-medium text-gray-500 dark:text-gray-400">
            <ArrowLeft className="w-4 h-4" /> Back to roles
          </button>
          <Link to="/">
            <Button className="w-full text-white rounded-full font-semibold" style={{ background: "#2A7B6F" }}>
              Go to homepage
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="waitlist-form">
      {backButton}
      <h2 className="text-2xl font-black mb-1 text-[#1A2E2A] dark:text-[#DFF2EE]">Join the waitlist</h2>
      <p className="text-sm mb-6 text-gray-500 dark:text-gray-400">Invite-only — we&apos;ll reach out when slots open.</p>
      <form onSubmit={submit} className="flex flex-col gap-5">

        {/* Full Name */}
        <div>
          <Label htmlFor="wl-name" className="block text-sm font-bold mb-2 text-[#1A2E2A] dark:text-[#DFF2EE]">
            Full Name
          </Label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" style={{ width: 18, height: 18 }} />
            <Input
              id="wl-name"
              placeholder="Your full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className="h-auto text-sm placeholder:text-gray-400 focus-visible:ring-offset-0"
              style={INPUT_STYLE}
              data-testid="waitlist-name-input"
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <Label htmlFor="wl-email" className="block text-sm font-bold mb-2 text-[#1A2E2A] dark:text-[#DFF2EE]">
            Email
          </Label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" style={{ width: 18, height: 18 }} />
            <Input
              id="wl-email"
              type="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className="h-auto text-sm placeholder:text-gray-400 focus-visible:ring-offset-0"
              style={INPUT_STYLE}
              data-testid="waitlist-email-input"
            />
          </div>
        </div>

        {/* Mobile number */}
        <div>
          <Label htmlFor="wl-phone" className="block text-sm font-bold mb-2 text-[#1A2E2A] dark:text-[#DFF2EE]">
            Mobile number
          </Label>
          <div className="relative">
            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" style={{ width: 18, height: 18 }} />
            <Input
              id="wl-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              required
              placeholder="+91 98765 43210"
              inputMode="tel"
              pattern="^[\d +()\-]{10,}$"
              className="h-auto text-sm placeholder:text-gray-400 focus-visible:ring-offset-0"
              style={INPUT_STYLE}
              data-testid="waitlist-phone-input"
            />
          </div>
        </div>

        {/* Clinic / Organisation */}
        <div>
          <Label htmlFor="wl-org" className="block text-sm font-bold mb-2 text-[#1A2E2A] dark:text-[#DFF2EE]">
            Clinic / Organisation{" "}
            <span className="font-normal text-gray-400 dark:text-gray-500">(optional)</span>
          </Label>
          <div className="relative">
            <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" style={{ width: 18, height: 18 }} />
            <Input
              id="wl-org"
              value={form.organization}
              onChange={(e) => setForm({ ...form, organization: e.target.value })}
              placeholder="e.g. LittleSteps Therapy"
              className="h-auto text-sm placeholder:text-gray-400 focus-visible:ring-offset-0"
              style={INPUT_STYLE}
            />
          </div>
        </div>

        {/* Anything to know */}
        <div>
          <Label htmlFor="wl-note" className="block text-sm font-bold mb-2 text-[#1A2E2A] dark:text-[#DFF2EE]">
            Anything you&apos;d like us to know?{" "}
            <span className="font-normal text-gray-400 dark:text-gray-500">(optional)</span>
          </Label>
          <div className="relative">
            <MessageSquare className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" style={{ width: 18, height: 18 }} />
            <Textarea
              id="wl-note"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              rows={1}
              placeholder="Role, specialisation, number of children…"
              className="text-sm placeholder:text-gray-400 resize-none focus-visible:ring-offset-0 !min-h-0"
              style={INPUT_STYLE}
            />
          </div>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={loading}
          className="h-auto w-full text-white rounded-full font-bold text-base shadow-none disabled:opacity-50"
          style={{ background: "#2A7B6F", paddingTop: "0.9rem", paddingBottom: "0.9rem" }}
          data-testid="waitlist-submit-btn"
        >
          <Mail className="w-4 h-4 mr-2 shrink-0" />
          {loading ? "Joining…" : "Join the waitlist"}
        </Button>
      </form>

      <PrivacyBadge />
    </div>
  );
}
